// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { SettingsService } from '../services/settings.service';
// shui has no typings
// eslint-disable-next-line
// @ts-ignore
export { DateFormatHelper } from '@shui/core/utils/date-format-helper.js';
// eslint-disable-next-line
// @ts-ignore
import { DateFormatHelper } from '@shui/core/utils/date-format-helper.js';
import { Subject, takeUntil, tap } from 'rxjs';

/**
 * kepps display in datepickers in sync with settings
 * directive applied to all sh-datepicker components
 */
@Directive({
  selector: 'sh-datepicker',
  standalone: true,
})
export class ShuiDatepickerDirective implements OnDestroy, OnChanges {
  @Input() betterValue?: [Date, Date];

  destroy$ = new Subject<void>();

  constructor(
    private el: ElementRef,
    private settingsController: SettingsService
  ) {
    this.updateElement();

    // emits our "betterChanged" event
    const observer = new MutationObserver((mutationList) => {
      for (const { target, type, attributeName, oldValue } of mutationList) {
        if (type === 'attributes') {
          const newValue = (target as unknown as { value: string }).value;
          if (attributeName === 'value' && oldValue !== newValue) {
            if (newValue === '') {
              // clear;
              (target as Element).setAttribute('value', oldValue || '');
              target.dispatchEvent(
                new CustomEvent('betterCleared', {
                  bubbles: true,
                  composed: true,
                })
              );
            } else {
              const value = (
                target as unknown as { standardValue: [Date, Date] | [] }
              ).standardValue;

              target.dispatchEvent(
                new CustomEvent('betterChanged', {
                  detail: value,
                  bubbles: true,
                  composed: true,
                })
              );
            }
          }
        }
      }
    });

    // Start observing the target node for configured mutations
    observer.observe(this.el.nativeElement, {
      attributes: true,
      childList: false,
      subtree: false,
    });

    settingsController.dateFormat$
      .pipe(
        tap(() => this.updateElement()),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.betterValue) {
      try {
        this.el.nativeElement.setAttribute(
          'value',
          this.stringifyRange(...this.betterValue!)
        );
        this.el.nativeElement.removeAttribute('error');
        this.el.nativeElement.removeAttribute('error-message');
      } catch {
        this.el.nativeElement.setAttribute('error', '');
        this.el.nativeElement.setAttribute(
          'error-message',
          'Invalid Date Format'
        );
      }
    }
  }

  updateElement(): void {
    const currentDateFormat = this.settingsController.dateFormat$.getValue();
    const picker = this.el.nativeElement;
    let previousDates: undefined | [Date, Date] = undefined;
    if (picker.value && picker.standardValue) {
      previousDates = [...picker.standardValue] as [Date, Date];
    }
    picker.value = undefined;
    picker.setAttribute('format', currentDateFormat.shuiFormat);
    picker.setAttribute('separator', currentDateFormat.shuiSeparator);
    picker.setAttribute('locale', currentDateFormat.shuiLocale);

    if (previousDates) {
      picker.value = this.stringifyRange(previousDates[0], previousDates[1]);
    }
  }

  stringifyRange(from: Date, to: Date): string {
    const { shuiFormat, shuiSeparator, shuiLocale } =
      this.settingsController.dateFormat$.getValue();
    // shui has no typings
    // eslint-disable-next-line
    // @ts-ignore
    const fromstr = DateFormatHelper.getFormattedDate(
      from,
      shuiFormat,
      shuiSeparator,
      shuiLocale
    );
    // shui has no typings
    // eslint-disable-next-line
    // @ts-ignore
    const tostr = DateFormatHelper.getFormattedDate(
      to,
      shuiFormat,
      shuiSeparator,
      shuiLocale
    );
    return `${fromstr} - ${tostr}`;
  }
}
